import React, { useState, FC, useEffect } from 'react';
import { buildLocStoreKeys } from 'store/types';

interface IContextState {
  setWorkMode: (mode: string | null, history?: any) => void;
  setWorkModeName: (name: string | null) => void;
  setAltWorkModeName: (name: string | null) => void;
  workMode: string | null;
  workModeName: string | null;
  altWorkModeName: string | null;
}

export const WorkModeContext = React.createContext<IContextState>({
  setWorkMode: () => {},
  setWorkModeName: () => {},
  setAltWorkModeName: () => {},
  workMode: null,
  workModeName: null,
  altWorkModeName: null,
});

interface IProps {
  children: React.ReactNode;
}

export const WorkModeProvider: FC<IProps> = ({ children }: IProps) => {
  const LocStoreKeys = buildLocStoreKeys();

  const [workMode, setWorkMode] = useState(window.localStorage.getItem(LocStoreKeys.WORK_MODE));
  const [workModeName, setWorkModeName] = useState(window.localStorage.getItem(LocStoreKeys.WORK_MODE_NAME));
  const [altWorkModeName, setAltWorkModeName] = useState(window.localStorage.getItem(LocStoreKeys.ALT_WORK_MODE_NAME));

  /**
   * Subscribe to local storage updates to handle the case
   * if client pages are open in more than one tab
   * and the work mode has been switched. The switch should
   * affect all open client tabs
   */
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.storageArea === localStorage && e.key === LocStoreKeys.WORK_MODE) {
        setWorkMode(e.newValue);
      }
      if (e.storageArea === localStorage && e.key === LocStoreKeys.WORK_MODE_NAME) {
        setWorkModeName(e.newValue);
      }
      if (e.storageArea === localStorage && e.key === LocStoreKeys.ALT_WORK_MODE_NAME) {
        setAltWorkModeName(e.newValue);
      }
      /**
       * TODO - handle redirecting to work mode specific routes
       */
    };
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('storage', handler);
    };
  });

  const setWorkModeStateAndStore = (mode: string | null, history?: any) => {
    setWorkMode(prevMode => {
      // On work mode switch redirect to /home
      if (!!prevMode && !!mode && !!history) history.push('/home');
      return mode;
    });

    if (mode) return window.localStorage.setItem(LocStoreKeys.WORK_MODE, mode);

    return window.localStorage.removeItem(LocStoreKeys.WORK_MODE);
  };

  const setWorkModeNameStateAndStore = (name: string | null) => {
    setWorkModeName(name);
    if (name) return window.localStorage.setItem(LocStoreKeys.WORK_MODE_NAME, name);

    return window.localStorage.removeItem(LocStoreKeys.WORK_MODE_NAME);
  };

  const setAltWorkModeNameStateAndStore = (name: string | null) => {
    setAltWorkModeName(name);
    if (name) return window.localStorage.setItem(LocStoreKeys.ALT_WORK_MODE_NAME, name);

    return window.localStorage.removeItem(LocStoreKeys.ALT_WORK_MODE_NAME);
  };

  return (
    <WorkModeContext.Provider
      value={{
        workMode,
        setWorkMode: setWorkModeStateAndStore,
        workModeName,
        setWorkModeName: setWorkModeNameStateAndStore,
        altWorkModeName,
        setAltWorkModeName: setAltWorkModeNameStateAndStore,
      }}
    >
      {children}
    </WorkModeContext.Provider>
  );
};

export function useWorkModeContext(): IContextState {
  return React.useContext(WorkModeContext);
}
